import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PlusCircle, X, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useOddsFormat } from '../../contexts/OddsFormatContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateTipForm = ({ serviceId, onTipCreated }) => {
  const { getOddsPlaceholder, convertToDecimal, convertOdds } = useOddsFormat();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  
  // Dropdown data
  const [leagues, setLeagues] = useState([]);
  const [allTeams, setAllTeams] = useState({});
  const [markets, setMarkets] = useState([]);
  const [customMarkets, setCustomMarkets] = useState([]);
  
  const [matches, setMatches] = useState([{
    league: '',
    home_team: '',
    away_team: '',
    market: '',
    customMarket: '',
    odds: '',
    prediction: '',
    match_date: ''
  }]);
  
  const [tipData, setTipData] = useState({
    tip_date: '',
    stake_recommendation: 5,
    confidence_level: 'Medium',
    analysis: '',
    price: 0,
    status: 'pending'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const [marketsRes, leaguesRes, customMarketsRes] = await Promise.all([
        axios.get(`${API}/data/markets`, { headers: { Authorization: `Bearer ${token}` } }),
        // Load only popular leagues for performance
        axios.get(`${API}/data/leagues?popular_only=true&limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/my-custom-markets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setMarkets(marketsRes.data.markets);
      setLeagues(leaguesRes.data.leagues);
      setCustomMarkets(customMarketsRes.data.markets || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load form data');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchTeams = async (league) => {
    if (allTeams[league]) return; // Already fetched
    
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/data/teams/${encodeURIComponent(league)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllTeams(prev => ({ ...prev, [league]: response.data.teams }));
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const addMatch = () => {
    setMatches([...matches, {
      league: '',
      home_team: '',
      away_team: '',
      market: '',
      customMarket: '',
      odds: '',
      prediction: '',
      match_date: ''
    }]);
  };

  const removeMatch = (index) => {
    if (matches.length > 1) {
      setMatches(matches.filter((_, i) => i !== index));
    }
  };

  const updateMatch = (index, field, value) => {
    const updated = [...matches];
    updated[index][field] = value;
    
    // Fetch teams when league is selected
    if (field === 'league' && value) {
      fetchTeams(value);
    }
    
    setMatches(updated);
  };

  const calculateTotalOdds = () => {
    let total = 1;
    matches.forEach(match => {
      if (match.odds) {
        const decimalOdds = convertToDecimal(match.odds);
        if (decimalOdds && decimalOdds >= 1) {
          total *= decimalOdds;
        }
      }
    });
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Save custom markets if any
    const token = localStorage.getItem('session_token');
    const processedMatches = [];
    const now = new Date();
    let earliestMatchDate = null;
    
    for (let match of matches) {
      let finalMarket = match.market === 'Custom' ? match.customMarket : match.market;
      
      // Save custom market
      if (match.market === 'Custom' && match.customMarket) {
        try {
          await axios.post(`${API}/data/add-custom-market?market=${encodeURIComponent(match.customMarket)}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!customMarkets.includes(match.customMarket)) {
            setCustomMarkets([...customMarkets, match.customMarket]);
          }
        } catch (error) {
          console.error('Error saving custom market:', error);
        }
      }
      
      if (!match.home_team || !match.away_team || !finalMarket || !match.odds) {
        toast.error('Please fill all match details');
        setLoading(false);
        return;
      }
      
      // Validate match date/time
      if (!match.match_date) {
        toast.error(`Please select date and time for match: ${match.home_team} vs ${match.away_team}`);
        setLoading(false);
        return;
      }
      
      const matchDateTime = new Date(match.match_date);
      if (matchDateTime <= now) {
        toast.error(`Match date/time must be in the future: ${match.home_team} vs ${match.away_team}`);
        setLoading(false);
        return;
      }
      
      // Track earliest match date for tip_date
      if (!earliestMatchDate || matchDateTime < earliestMatchDate) {
        earliestMatchDate = matchDateTime;
      }
      
      // Convert odds to decimal for backend storage
      const decimalOdds = convertToDecimal(match.odds);
      if (!decimalOdds || decimalOdds < 1) {
        toast.error(`Invalid odds for match ${match.home_team} vs ${match.away_team}`);
        setLoading(false);
        return;
      }
      
      processedMatches.push({
        ...match,
        market: finalMarket,
        odds: decimalOdds
      });
    }

    if (!tipData.analysis) {
      toast.error('Please add analysis');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/tipster-services/${serviceId}/tips`,
        {
          ...tipData,
          tip_date: earliestMatchDate.toISOString(), // Use earliest match date as tip_date
          matches: processedMatches,
          price: parseFloat(tipData.price) || 0
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Tip created successfully!');
      
      // Reset form
      setMatches([{
        league: '',
        home_team: '',
        away_team: '',
        market: '',
        customMarket: '',
        odds: '',
        prediction: ''
      }]);
      setTipData({
        tip_date: '',
        stake_recommendation: 5,
        confidence_level: 'Medium',
        analysis: '',
        price: 0,
        status: 'pending'
      });

      if (onTipCreated) {
        onTipCreated(response.data);
      }
    } catch (error) {
      console.error('Error creating tip:', error);
      toast.error(error.response?.data?.detail || 'Failed to create tip');
    } finally {
      setLoading(false);
    }
  };

  const totalOdds = calculateTotalOdds();
  const isParlay = matches.length > 1;

  return (
    <Card className="glass-dark border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-400" />
          Create New Tip
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Matches Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <Label className="text-white text-lg">
                {isParlay ? `Parlay (${matches.length} Matches)` : 'Single Match'}
              </Label>
              <Button type="button" onClick={addMatch} size="sm" className="flex items-center gap-2">
                <PlusCircle className="w-4 h-4" />
                Add Match
              </Button>
            </div>

            {matches.map((match, index) => (
              <Card key={index} className="mb-4 bg-white/5 border-white/10">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-white font-semibold">Match {index + 1}</h4>
                    {matches.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMatch(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">League</Label>
                      <Select 
                        value={match.league} 
                        onValueChange={(value) => updateMatch(index, 'league', value)}
                        disabled={dataLoading}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue placeholder="Select league" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {leagues.sort().map((league) => (
                            <SelectItem key={league} value={league}>{league}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Market</Label>
                      <Select 
                        value={match.market} 
                        onValueChange={(value) => updateMatch(index, 'market', value)}
                        disabled={dataLoading}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue placeholder="Select market" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {markets.sort().map((market) => (
                            <SelectItem key={market} value={market}>{market}</SelectItem>
                          ))}
                          {customMarkets.map((market) => (
                            <SelectItem key={`custom-${market}`} value={market}>{market} (Custom)</SelectItem>
                          ))}
                          <SelectItem value="Custom">+ Add Custom Market</SelectItem>
                        </SelectContent>
                      </Select>
                      {match.market === 'Custom' && (
                        <Input
                          value={match.customMarket}
                          onChange={(e) => updateMatch(index, 'customMarket', e.target.value)}
                          placeholder="Enter custom market"
                          className="bg-white/10 border-white/20 text-white mt-2"
                        />
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Home Team</Label>
                      {match.league && allTeams[match.league] ? (
                        <Select 
                          value={match.home_team} 
                          onValueChange={(value) => updateMatch(index, 'home_team', value)}
                        >
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue placeholder="Select home team" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {allTeams[match.league].sort().map((team) => (
                              <SelectItem key={team} value={team}>{team}</SelectItem>
                            ))}
                            <SelectItem value="Custom">+ Add Custom Team</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={match.home_team}
                          onChange={(e) => updateMatch(index, 'home_team', e.target.value)}
                          placeholder={match.league ? "Loading teams..." : "Select league first"}
                          className="bg-white/10 border-white/20 text-white"
                          disabled={!match.league}
                          required
                        />
                      )}
                      {match.home_team === 'Custom' && (
                        <Input
                          value={match.home_team}
                          onChange={(e) => updateMatch(index, 'home_team', e.target.value)}
                          placeholder="Enter custom home team"
                          className="bg-white/10 border-white/20 text-white mt-2"
                        />
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Away Team</Label>
                      {match.league && allTeams[match.league] ? (
                        <Select 
                          value={match.away_team} 
                          onValueChange={(value) => updateMatch(index, 'away_team', value)}
                        >
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue placeholder="Select away team" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {allTeams[match.league].sort().map((team) => (
                              <SelectItem key={team} value={team}>{team}</SelectItem>
                            ))}
                            <SelectItem value="Custom">+ Add Custom Team</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={match.away_team}
                          onChange={(e) => updateMatch(index, 'away_team', e.target.value)}
                          placeholder={match.league ? "Loading teams..." : "Select league first"}
                          className="bg-white/10 border-white/20 text-white"
                          disabled={!match.league}
                          required
                        />
                      )}
                      {match.away_team === 'Custom' && (
                        <Input
                          value={match.away_team}
                          onChange={(e) => updateMatch(index, 'away_team', e.target.value)}
                          placeholder="Enter custom away team"
                          className="bg-white/10 border-white/20 text-white mt-2"
                        />
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Odds</Label>
                      <Input
                        type="text"
                        value={match.odds}
                        onChange={(e) => updateMatch(index, 'odds', e.target.value)}
                        placeholder={getOddsPlaceholder()}
                        className="bg-white/10 border-white/20 text-white"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Prediction</Label>
                      <Input
                        value={match.prediction}
                        onChange={(e) => updateMatch(index, 'prediction', e.target.value)}
                        placeholder="e.g., Home Win"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Match Start Date & Time *</Label>
                      <Input
                        type="datetime-local"
                        value={match.match_date}
                        onChange={(e) => updateMatch(index, 'match_date', e.target.value)}
                        min={(() => {
                          const now = new Date();
                          const year = now.getFullYear();
                          const month = String(now.getMonth() + 1).padStart(2, '0');
                          const day = String(now.getDate()).padStart(2, '0');
                          const hours = String(now.getHours()).padStart(2, '0');
                          const minutes = String(now.getMinutes()).padStart(2, '0');
                          return `${year}-${month}-${day}T${hours}:${minutes}`;
                        })()}
                        className="bg-white/10 border-white/20 text-white"
                        required
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Total Odds Display */}
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-white font-semibold">Total Odds:</span>
              <span className="text-2xl text-blue-400 font-bold">{convertOdds(totalOdds)}</span>
            </div>
            {isParlay && (
              <p className="text-xs text-gray-400 mt-2">
                Parlay with {matches.length} selections
              </p>
            )}
          </div>

          {/* Tip Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white">Stake Recommendation (Units)</Label>
              <Input
                type="number"
                min="1"
                value={tipData.stake_recommendation}
                onChange={(e) => setTipData({...tipData, stake_recommendation: parseInt(e.target.value) || 1})}
                className="bg-white/10 border-white/20 text-white"
                list="stake-suggestions"
              />
              <datalist id="stake-suggestions">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(val => (
                  <option key={val} value={val} />
                ))}
              </datalist>
              <p className="text-xs text-gray-400 mt-1">Recommended stake: 1-20 units (can input higher if needed)</p>
            </div>

            <div>
              <Label className="text-white">Confidence Level</Label>
              <select
                value={tipData.confidence_level}
                onChange={(e) => setTipData({...tipData, confidence_level: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            <div>
              <Label className="text-white">Price (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={tipData.price}
                onChange={(e) => setTipData({...tipData, price: e.target.value})}
                className="bg-white/10 border-white/20 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">0 = Free, > 0 = Paid</p>
            </div>

            <div>
              <Label className="text-white">Status</Label>
              <select
                value={tipData.status}
                onChange={(e) => setTipData({...tipData, status: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
              >
                <option value="pending">Pending</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="void">Void (Refunded)</option>
              </select>
            </div>
          </div>

          {/* Analysis */}
          <div>
            <Label className="text-white">Analysis / Reasoning *</Label>
            <Textarea
              value={tipData.analysis}
              onChange={(e) => setTipData({...tipData, analysis: e.target.value})}
              placeholder="Explain why you're making this tip..."
              className="bg-white/10 border-white/20 text-white min-h-[120px]"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Share your reasoning with subscribers</p>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {loading ? 'Creating...' : 'Create Tip'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default CreateTipForm;
